import type { ReactNode } from 'react';
import { Button, Modal } from 'rsuite';
import styles from './ActionFeedbackDialog.module.scss';

export type ActionFeedbackDialogDefaultType = 'success' | 'warning' | 'error' | 'info';
export type ActionFeedbackDialogType = ActionFeedbackDialogDefaultType | 'other';

type ActionFeedbackDialogAction = {
  label: string;
  onClick: () => void;
};

type ActionFeedbackDialogBaseProps = {
  open: boolean;
  title: string;
  description: ReactNode;
  primaryAction?: ActionFeedbackDialogAction;
  secondaryAction?: ActionFeedbackDialogAction;
  onClose?: () => void;
  width?: string | number;
  dismissible?: boolean;
};

type ActionFeedbackDialogDefaultProps = ActionFeedbackDialogBaseProps & {
  type: ActionFeedbackDialogDefaultType;
  icon?: never;
};

type ActionFeedbackDialogOtherProps = ActionFeedbackDialogBaseProps & {
  type: 'other';
  icon: ReactNode;
};

export type ActionFeedbackDialogProps = ActionFeedbackDialogDefaultProps | ActionFeedbackDialogOtherProps;

const iconContent: Record<ActionFeedbackDialogDefaultType, string> = {
  success: '',
  warning: '!',
  error: '',
  info: 'i',
};

export const ActionFeedbackDialog = ({
  open,
  type,
  title,
  description,
  primaryAction,
  secondaryAction,
  onClose,
  width,
  dismissible = true,
  ...props
}: ActionFeedbackDialogProps) => {
  const handleHide = onClose ?? secondaryAction?.onClick ?? (() => undefined);
  const icon = type === 'other' ? props.icon : iconContent[type];
  const hasActions = Boolean(primaryAction || secondaryAction);

  return (
    <Modal
      show={open}
      onHide={dismissible ? handleHide : undefined}
      className={styles.modal}
      style={{ width }}
      size="sm"
      keyboard={dismissible}
      backdrop={dismissible || 'static'}
    >
      <Modal.Body>
        <div className={`${styles.iconWrap} ${styles[type]}`} role="img" aria-label={type}>
          {icon}
        </div>

        <h3 className={styles.title}>{title}</h3>

        <div className={styles.description}>{description}</div>
      </Modal.Body>

      {hasActions ? (
        <Modal.Footer className={styles.footer}>
          {secondaryAction ? (
            <Button appearance="ghost" onClick={secondaryAction.onClick} className={styles.btnGhost}>
              {secondaryAction.label}
            </Button>
          ) : null}

          {primaryAction ? (
            <Button appearance="primary" onClick={primaryAction.onClick} className={styles.btnPrimary}>
              {primaryAction.label}
            </Button>
          ) : null}
        </Modal.Footer>
      ) : null}
    </Modal>
  );
};
